import React, { useState, useEffect } from 'react';
import { Bell, Clock, Check, Repeat } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const UpcomingReminders = ({ habits, completions, onToggleCompletion }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const todayStr = currentTime.toISOString().split('T')[0];

  const getReminderTimes = (habit) => {
    if (!habit.reminderTime) return [];
    
    const times = [];
    const [baseHour, baseMin] = habit.reminderTime.split(':').map(Number);
    
    // Default: once a day
    if (!habit.reminderFrequency || habit.reminderFrequency === 'once') {
      times.push({
        time: habit.reminderTime,
        isRecurring: false
      });
    }
    
    // Interval-based reminders
    else if (habit.reminderFrequency === 'interval' && habit.reminderInterval) {
      const interval = habit.reminderInterval;
      let currentHour = baseHour;
      let index = 0;
      
      // Generate reminders from base time until end of day
      while (currentHour < 24) {
        const timeStr = `${String(currentHour).padStart(2, '0')}:${String(baseMin).padStart(2, '0')}`;
        times.push({
          time: timeStr,
          isRecurring: true,
          index: index
        });
        currentHour += interval;
        index++;
      }
    }
    
    return times;
  };

  // Get all reminders with their statuses
  const allReminders = [];
  
  habits.forEach(habit => {
    if (!habit.reminderTime) return;
    
    const isCompleted = completions.some(
      c => c.habitId === habit.id && c.date === todayStr
    );
    
    const reminderTimes = getReminderTimes(habit);
    
    reminderTimes.forEach(({ time, isRecurring, index }) => {
      const [reminderHour, reminderMin] = time.split(':').map(Number);
      const reminderDate = new Date(currentTime);
      reminderDate.setHours(reminderHour, reminderMin, 0, 0);
      
      const diffMs = reminderDate - currentTime;
      const diffMins = Math.floor(diffMs / 60000);
      
      let status = 'upcoming';
      if (diffMins < -60) status = 'past'; // More than 1 hour ago
      else if (diffMins >= -5 && diffMins <= 5) status = 'now'; // Within 5 mins
      else if (diffMins < 0) status = 'recent'; // Less than 1 hour ago
      
      allReminders.push({
        habit,
        time,
        isCompleted,
        isRecurring,
        reminderIndex: index,
        diffMins,
        status,
        reminderDate
      });
    });
  });

  // Filter and sort
  const visibleReminders = allReminders
    .filter(r => !r.isCompleted && r.status !== 'past')
    .sort((a, b) => a.diffMins - b.diffMins);

  if (visibleReminders.length === 0) return null;

  return (
    <Card className="p-4 bg-gradient-to-br from-amber-50/50 via-yellow-50/30 to-orange-50/50 dark:from-amber-950/20 dark:via-yellow-950/20 dark:to-orange-950/20 border-amber-200/50 dark:border-amber-800/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/50">
            <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="font-semibold text-foreground">Lembretes de Hoje</h3>
        </div>
        <span className="text-xs text-muted-foreground">
          {visibleReminders.length} pendente{visibleReminders.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {visibleReminders.map((reminder, idx) => (
          <div
            key={`${reminder.habit.id}-${reminder.time}`}
            className={cn(
              'flex items-center justify-between p-3 rounded-lg transition-all',
              reminder.status === 'now' 
                ? 'bg-amber-100 dark:bg-amber-900/40 border-2 border-amber-500 dark:border-amber-600 animate-pulse'
                : 'bg-white/60 dark:bg-white/5 border border-border/50'
            )}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex flex-col items-center gap-0.5">
                <Clock className={cn(
                  'w-4 h-4 flex-shrink-0',
                  reminder.status === 'now' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
                )} />
                {reminder.isRecurring && (
                  <Repeat className="w-3 h-3 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm text-foreground truncate">
                    {reminder.habit.name}
                  </p>
                  {reminder.isRecurring && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 whitespace-nowrap">
                      {reminder.reminderIndex + 1}º
                    </span>
                  )}
                </div>
                <p className={cn(
                  'text-xs',
                  reminder.status === 'now' ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-muted-foreground'
                )}>
                  {reminder.status === 'now' && 'Agora! '}
                  {reminder.status === 'recent' && 'Há pouco • '}
                  {reminder.time}
                  {reminder.status === 'upcoming' && reminder.diffMins > 0 && ` • em ${reminder.diffMins}min`}
                  {reminder.isRecurring && ` • a cada ${reminder.habit.reminderInterval}h`}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant={reminder.status === 'now' ? 'default' : 'ghost'}
              onClick={() => onToggleCompletion(reminder.habit.id, currentTime, null)}
              className="flex-shrink-0"
            >
              <Check className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Summary */}
      {visibleReminders.length > 3 && (
        <div className="mt-3 pt-3 border-t border-border/50 text-xs text-muted-foreground text-center">
          Você tem {visibleReminders.length} lembretes configurados para hoje
        </div>
      )}
    </Card>
  );
};

export default UpcomingReminders;
